/**
 * Template editor: the full template definition (marker tree + question copy) served with company facts
 * pre-filled, LLM draft-assist for free-text passages, and generation of the finished .docx onto a
 * checklist slot.
 *
 * The definition is resolved CLIENT-side for the live preview (see ../templateResolver.ts); generation
 * re-resolves server-side from the same answers, so the authoritative document (locked statutory wording
 * included) never depends on client-resolved text.
 */

import { apiGet, apiPost } from '../apiClient';

/**
 * A node in a template's marker tree. One shape covers every kind, discriminated by `t`:
 * `text` (raw markdown in `v`), `ph` (placeholder `n`), `if` (marker `m` + children `c`),
 * `choose` (branches in `c`), `branch` (label/preview + children), `locked` (children),
 * `draft` (note `id`), `repeat` (caption `note` + children).
 */
export interface TemplateNode {
  t: 'text' | 'ph' | 'if' | 'choose' | 'branch' | 'locked' | 'draft' | 'repeat';
  v?: string | null;
  n?: string | null;
  m?: string | null;
  id?: string | null;
  note?: string | null;
  label?: string | null;
  preview?: string | null;
  c?: TemplateNode[] | null;
}

/** Question copy for one placeholder. */
export interface TemplateField {
  label: string;
  whatToEnter: string;
  example: string;
  /** text | textarea | date | money | number | email. */
  kind: string;
  /** Best-practice default (already resolved into `prefill` server-side); kept for reference. */
  default?: string | null;
}

/** An authored drafting note for a free-text DRAFT passage. */
export interface TemplateDraft {
  title: string;
  guidance: string;
  example: string;
}

export interface TemplateDefinition {
  slug: string;
  title: string;
  family: string;
  oneLine: string;
  necessity: string;
  fields: Record<string, TemplateField>;
  conditions: Record<string, { question: string }>;
  drafts: Record<string, TemplateDraft>;
  body: TemplateNode[];
}

/** Where a pre-filled value came from, for the field tags. */
export type PrefillSource = 'saved' | 'profile' | 'suggested';

/** GET …/templates/{slug}: the definition plus the merged pre-fill and its per-key sources. */
export interface TemplateBundle {
  definition: TemplateDefinition;
  prefill: Record<string, string>;
  sources: Record<string, PrefillSource>;
}

/**
 * The answers the editor collects. `values` is keyed by: a placeholder name for a field, `if:{marker}`
 * (true/false), `choice:{n}` (branch index), `draft:{id}` (the passage text). `repeats` holds register
 * rows keyed by `repeat:{n}`, each row a map of placeholder name to value.
 */
export interface TemplateAnswers {
  values: Record<string, string>;
  repeats: Record<string, Record<string, string>[]>;
}

/** GET /api/companies/{companyId}/templates/{slug} — the editor bundle. */
export function getTemplateDefinition(
  companyId: string,
  slug: string,
  employeeId?: string,
): Promise<TemplateBundle> {
  const q = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : '';
  return apiGet<TemplateBundle>(
    `/api/companies/${encodeURIComponent(companyId)}/templates/${encodeURIComponent(slug)}${q}`,
  );
}

/**
 * POST …/templates/{slug}/draft — draft one free-text passage with LLM assist, grounded in the filled
 * field values. `draftId` is e.g. "D1" (no `draft:` prefix).
 */
export function draftTemplateSection(
  companyId: string,
  slug: string,
  draftId: string,
  values: Record<string, string>,
): Promise<{ text: string }> {
  return apiPost<{ text: string }>(
    `/api/companies/${encodeURIComponent(companyId)}/templates/${encodeURIComponent(slug)}/draft`,
    { draftId, values },
  );
}

/**
 * POST …/templates/{slug}/generate — resolve server-side, render the .docx, attach it to the slot.
 * Returns the new document id.
 */
export function generateTemplateDocument(
  companyId: string,
  slug: string,
  answers: TemplateAnswers,
  scope?: { employeeId?: string; caseId?: string },
): Promise<{ documentId: string }> {
  return apiPost<{ documentId: string }>(
    `/api/companies/${encodeURIComponent(companyId)}/templates/${encodeURIComponent(slug)}/generate`,
    { answers, employeeId: scope?.employeeId, caseId: scope?.caseId },
  );
}
